import {Popover} from '@dropbox/dig-components/popover';
import {atoms} from '@dropbox/dig-foundations';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {growthbookCacheAtom} from 'atoms/growthbook';
import {ProductRoadmapService, type Workstream} from 'client';
import {useAtomValue} from 'jotai';
import {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {getService} from 'utilities';
import {productRoadmapQueryKey, useProductRoadmapDocument} from 'views/productRoadmap/hooks';
import {invalidateNavTrees} from 'views/workstreams/page/useNavTree';
import {logWorkstreamsInteraction} from 'views/workstreams/page/workstreamsAnalytics';

import {WorkstreamModalFieldError} from './WorkstreamModalField';
import {WorkstreamProductRoadmapModal} from './WorkstreamProductRoadmapModal';
import {WorkstreamRoadmapChip} from './WorkstreamRoadmapChip';
import {WorkstreamRoadmapEmbed} from './WorkstreamRoadmapEmbed';
import {RoadmapLinkForm, roadmapSaveErrorMessage} from './WorkstreamRoadmapLinkForm';
import {WorkstreamRoadmapMenu} from './WorkstreamRoadmapMenu';

type RoadmapWorkstream = Pick<Workstream, 'id'>;

/**
 * The detail row's Roadmap chip. Without a roadmap, a click opens a modal to paste
 * its link; with one, a menu to open it, change the link or remove it. A linked
 * roadmap also shows in the rail, after the row's narrative.
 */
export const WorkstreamProductRoadmapButton = ({
  workstream,
  embedSlot,
  onRoadmapContentVisibilityChange,
}: {
  workstream: RoadmapWorkstream;
  embedSlot?: HTMLElement | null;
  onRoadmapContentVisibilityChange?: (open: boolean) => void;
}) => {
  const {isWorkstreamProductRoadmapEnabled, isWorkstreamProductRoadmapIframeEnabled} =
    useAtomValue(growthbookCacheAtom);
  const queryClient = useQueryClient();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEmbedOpen, setIsEmbedOpen] = useState(false);
  const {data: roadmap, isError} = useProductRoadmapDocument({
    workstreamId: workstream.id,
    enabled: isWorkstreamProductRoadmapEnabled,
  });
  const remove = useMutation({
    mutationFn: (roadmapId: number) =>
      getService(ProductRoadmapService).deleteProductRoadmapApiV1ProductRoadmapRoadmapIdDelete(
        roadmapId
      ),
    onSuccess: () => {
      logWorkstreamsInteraction('roadmap_removed', {workstream_id: workstream.id});
      setIsEmbedOpen(false);
      invalidateNavTrees();
      return queryClient.invalidateQueries({
        queryKey: productRoadmapQueryKey({workstreamId: workstream.id}),
        exact: true,
      });
    },
  });

  const canEmbedRoadmap =
    isWorkstreamProductRoadmapIframeEnabled &&
    Boolean(roadmap?.iframe_url || roadmap?.dropbox_embed_app_key);
  const shouldShowRoadmapEmbed =
    isWorkstreamProductRoadmapEnabled && canEmbedRoadmap && isEmbedOpen;
  useEffect(() => {
    if (!canEmbedRoadmap) setIsEmbedOpen(false);
  }, [canEmbedRoadmap]);
  useEffect(() => {
    onRoadmapContentVisibilityChange?.(shouldShowRoadmapEmbed);
    return () => onRoadmapContentVisibilityChange?.(false);
  }, [onRoadmapContentVisibilityChange, shouldShowRoadmapEmbed]);

  if (!isWorkstreamProductRoadmapEnabled) return null;

  const openRoadmap = () => {
    if (!roadmap) return;
    logWorkstreamsInteraction('roadmap_link_opened', {workstream_id: workstream.id, from: 'chip'});
    window.open(roadmap.open_url, '_blank', 'noopener,noreferrer');
  };
  const embed =
    roadmap && shouldShowRoadmapEmbed ? (
      <WorkstreamRoadmapEmbed
        data={roadmap}
        disabled={remove.isPending}
        onOpen={openRoadmap}
        onEdit={() => setIsFormOpen(true)}
        onRemove={roadmap.id ? () => remove.mutate(roadmap.id!) : undefined}
      />
    ) : null;
  const shouldShowAddRoadmapModal = isWorkstreamProductRoadmapIframeEnabled && !roadmap;

  return (
    <>
      <span ref={anchorRef} className={atoms({display: 'inline-flex'})}>
        {roadmap && !canEmbedRoadmap ? (
          <WorkstreamRoadmapMenu
            disabled={remove.isPending}
            onOpen={openRoadmap}
            onEdit={() => setIsFormOpen(true)}
            onRemove={roadmap.id ? () => remove.mutate(roadmap.id!) : undefined}
          />
        ) : (
          <WorkstreamRoadmapChip
            showAddIcon={roadmap === null}
            withDropdownIcon={Boolean(roadmap)}
            isSelected={shouldShowRoadmapEmbed}
            isExpanded={shouldShowRoadmapEmbed}
            // Enabled while the lookup runs; the plus shows once there's no roadmap.
            disabled={isError}
            aria-expanded={
              roadmap ? shouldShowRoadmapEmbed : roadmap === null ? isFormOpen : undefined
            }
            onClick={() => {
              if (roadmap === null) setIsFormOpen(true);
              else if (roadmap) {
                const expanded = !isEmbedOpen;
                logWorkstreamsInteraction('roadmap_embed_toggled', {
                  workstream_id: workstream.id,
                  expanded,
                });
                setIsEmbedOpen(expanded);
              }
            }}
          />
        )}
      </span>
      {embedSlot ? createPortal(embed, embedSlot) : embed}
      {isFormOpen && shouldShowAddRoadmapModal && (
        <WorkstreamProductRoadmapModal
          workstream={workstream}
          onClose={() => setIsFormOpen(false)}
        />
      )}
      <Popover
        open={isFormOpen && !shouldShowAddRoadmapModal}
        anchorEl={anchorRef.current}
        onToggle={setIsFormOpen}
        // The link field takes focus itself (autoFocus), ready for a paste.
        shouldFocusContentOnOpen={false}
        placement="bottom-start"
        offsetDistance={8}
      >
        <Popover.Overlay>
          <Popover.Content>
            <Popover.Body>
              {isFormOpen && !shouldShowAddRoadmapModal && (
                <RoadmapLinkForm
                  workstream={workstream}
                  roadmap={roadmap ?? null}
                  onDone={() => setIsFormOpen(false)}
                />
              )}
            </Popover.Body>
          </Popover.Content>
        </Popover.Overlay>
      </Popover>
      {remove.isError && (
        <WorkstreamModalFieldError>
          {roadmapSaveErrorMessage(remove.error)}
        </WorkstreamModalFieldError>
      )}
    </>
  );
};
